import { decodeClinicalTimes } from '../clinicalTimeCodec';
import { jwtDecode } from 'jwt-decode';

// Helper function to get auth headers
// Only includes Authorization when token exists to avoid sending "Bearer null" after sign-out
export const getAuthHeaders = (additionalHeaders = {}) => {
    const token = localStorage.getItem('velaToken');
    if (token) {
        try {
            jwtDecode(token);
        } catch (error) {
            console.error('Error decoding token:', error);
        }
    }

    const headers = {
        "Content-Type": "application/json",
        ...additionalHeaders
    };
    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }
    return headers;
};

// Auth header without Content-Type — needed for FormData uploads where the
// browser must set the multipart boundary automatically.
// Only includes Authorization when token exists to avoid sending "Bearer null" after sign-out
export const getAuthHeaderOnly = () => {
    const token = localStorage.getItem('velaToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to get user ID from token
export const getUserIdFromToken = () => {
    const token = localStorage.getItem('velaToken');
    if (!token) return null;
    try {
        const decoded = jwtDecode(token);
        return decoded.sub;
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

// Helper function to get full user info from token
export const getUserInfoFromToken = () => {
    const token = localStorage.getItem('velaToken');
    if (!token) return null;
    
    try {
        const decoded = jwtDecode(token);
        
        // Try to find organizationId and referenceId from various possible claim names
        return {
            sub: decoded.sub,
            referenceId: decoded.sub || decoded.referenceId || decoded.ReferenceId || decoded.userId || decoded.UserId,
            organizationId: decoded.organizationId || decoded.OrganizationId || decoded.orgId || decoded.OrgId,
            organizationReferenceId: decoded.organizationReferenceId || decoded.OrganizationReferenceId,
            organizationRole: decoded.organizationRole || decoded.OrganizationRole,
            contextType: decoded.contextType || decoded.ContextType,
            contextReferenceId: decoded.contextReferenceId || decoded.ContextReferenceId,
            clientReferenceId: decoded.clientReferenceId || decoded.ClientReferenceId,
            role: decoded.role || decoded.Role || decoded['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
            email: decoded.email || decoded.Email,
            name: decoded.name || decoded.Name,
            firstName: decoded.firstName || decoded.FirstName,
            lastName: decoded.lastName || decoded.LastName
        };
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

const formatApiErrorValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) return value.filter(Boolean).join(' ');
    return JSON.stringify(value);
};

const getApiErrorDetails = async (response, fallback) => {
    const errorText = await response.text();
    let errorData = null;

    if (errorText) {
        try {
            errorData = JSON.parse(errorText);
        } catch {
            // Non-JSON error bodies are valid; surface their text below.
        }
    }

    let message;
    if (typeof errorData === 'string') {
        message = errorData || fallback;
    } else if (errorData && typeof errorData === 'object') {
        const validationErrors = errorData.errors
            ? Object.values(errorData.errors).flat().filter(Boolean).join(' ')
            : '';

        message = (
            formatApiErrorValue(errorData.message) ||
            formatApiErrorValue(errorData.error) ||
            validationErrors ||
            errorText ||
            fallback
        );
    } else {
        message = errorText || fallback;
    }

    return {
        message,
        code: errorData && typeof errorData === 'object' ? errorData.code : undefined,
        body: errorData
    };
};

const routeAccountActivationError = (errorDetails) => {
    if (!['EMAIL_VERIFICATION_REQUIRED', 'SANDBOX_TERMS_ACCEPTANCE_REQUIRED'].includes(errorDetails?.code)) {
        return;
    }

    const message = errorDetails.code === 'EMAIL_VERIFICATION_REQUIRED'
        ? 'Verify your email address before continuing.'
        : 'Sign in again to review the current Sandbox Evaluation Terms.';
    sessionStorage.setItem('velaActivationMessage', message);
    window.dispatchEvent(new CustomEvent('vela:account-activation-required', {
        detail: { code: errorDetails.code }
    }));
};

const getAuthenticationFailureReason = () => {
    const token = localStorage.getItem('velaToken');
    if (!token) return 'missing';

    try {
        const { exp } = jwtDecode(token);
        return exp && exp * 1000 <= Date.now() ? 'expired' : 'rejected';
    } catch {
        return 'invalid';
    }
};

export const createAuthenticationError = (response) => {
    const reason = getAuthenticationFailureReason();
    window.dispatchEvent(new CustomEvent('vela:session-expired', {
        detail: { reason }
    }));

    const error = new Error(reason === 'expired' ? 'Session expired' : 'Sign-in required');
    error.status = response.status;
    error.statusText = response.statusText;
    error.code = 'AUTHENTICATION_REQUIRED';
    return error;
};

export const handleResponse = async (response) => {
    if (!response.ok) {
        const deviceSaveFailed = response.headers.get('X-Sena-Device-Save-Failed') === 'true';
        if (deviceSaveFailed) {
            const body = await response.clone().json().catch(() => ({}));
            const error = new Error(body.error || 'Not saved on device. Keep this chart open and reconnect before leaving.');
            error.status = response.status;
            error.deviceSaveFailed = true;
            throw error;
        }
        const offlineDraftId = response.headers.get('X-Sena-Draft-Id');
        if (offlineDraftId) {
            const body = await response.clone().json().catch(() => ({}));
            const error = new Error(body.error || 'Saved on this device, but not yet synced to the server.');
            error.status = response.status;
            error.offlineDraftId = offlineDraftId;
            error.savedOnDevice = true;
            throw error;
        }
        if (response.status === 401) {
            throw createAuthenticationError(response);
        }
        const errorDetails = await getApiErrorDetails(response, `HTTP error! status: ${response.status}`);
        routeAccountActivationError(errorDetails);
        console.error('API Error:', {
            status: response.status,
            statusText: response.statusText,
            error: errorDetails.message
        });
        const error = new Error(errorDetails.message);
        error.status = response.status;
        error.statusText = response.statusText;
        error.code = errorDetails.code;
        error.body = errorDetails.body;
        throw error;
    }
    
    const contentType = response.headers.get('content-type');
    
    if (contentType && contentType.includes('application/json')) {
        return response.json().then(decodeClinicalTimes);
    } else {
        return { success: true };
    }
};

export const handleNoContentResponse = async (response) => {
    if (!response.ok) {
        const deviceSaveFailed = response.headers.get('X-Sena-Device-Save-Failed') === 'true';
        if (deviceSaveFailed) {
            const body = await response.clone().json().catch(() => ({}));
            const error = new Error(body.error || 'Not saved on device. Keep this chart open and reconnect before leaving.');
            error.status = response.status;
            error.deviceSaveFailed = true;
            throw error;
        }
        const offlineDraftId = response.headers.get('X-Sena-Draft-Id');
        if (offlineDraftId) {
            const body = await response.clone().json().catch(() => ({}));
            const error = new Error(body.error || 'Saved on this device, but not yet synced to the server.');
            error.status = response.status;
            error.offlineDraftId = offlineDraftId;
            error.savedOnDevice = true;
            throw error;
        }
        if (response.status === 401) {
            throw createAuthenticationError(response);
        }
        const errorDetails = await getApiErrorDetails(response, `HTTP error! status: ${response.status}`);
        routeAccountActivationError(errorDetails);
        console.error('Server error response:', errorDetails.message);
        const error = new Error(errorDetails.message);
        error.status = response.status;
        error.statusText = response.statusText;
        error.code = errorDetails.code;
        error.body = errorDetails.body;
        throw error;
    }
    // For 204 No Content responses, return success indicator
    if (response.status === 204) {
        return { success: true };
    }
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
        return response.json().then(decodeClinicalTimes);
    } else {
        return { success: true };
    }
};
